import qs from 'qs'

import { api, makeApiRequest } from 'api/_init'
import { unwrapEnvelope } from 'domain/shared/envelope'
import { mapIntegrationDetails } from './mappers'
import { AssetClass, Platform } from './schemas'

function withPlatformUserIds(endpoint, platformUserIds) {
  return platformUserIds ? `${endpoint}?platform_user_ids=${platformUserIds.join(',')}` : endpoint
}

const integrationAssetsApi = {
  // Unified, cross-platform (app/api/v4/integrations/connect/assets.py) —
  // one call returns Facebook, Instagram, and Google entries together.
  // Callers filter by platform + asset_class client-side, e.g. via
  // findByAssetClass from ./mappers.
  listAssetsAsync: async ({ platformUserIds, platforms } = {}) => {
    const params = {}
    if (platformUserIds) params.platform_user_ids = platformUserIds.join(',')
    if (platforms) params.platforms = platforms

    const query = Object.keys(params).length
      ? `/integrations/connect/assets?${qs.stringify(params, { arrayFormat: 'repeat' })}`
      : '/integrations/connect/assets'
      
    const response = await makeApiRequest(api, 'get', query)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapIntegrationDetails(data), status }
    }
    return { error, status }
  },

  // Unified enrich — dispatches per-asset by its `platform` field server-side
  // (Facebook/Instagram vs. Google), so a mixed-platform payload is fine.
  enrichAssetsAsync: async (assets) => {
    const response = await makeApiRequest(api, 'post', '/integrations/connect/assets/enrich', {
      assets,
    })
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapIntegrationDetails(data), status }
    }
    return { error, status }
  },

  // Facebook-scoped (app/api/v4/integrations/connect/facebook/assets/assets.py)
  // — same endpoints/behavior as the old metaAssetsApi. Kept platform-scoped
  // because refetch/task-status/confirm are Facebook OAuth-connect-flow
  // features with no unified equivalent.
  listFacebookAssetsAsync: async ({ is_enabled, platformUserIds } = {}) => {
    const params = new URLSearchParams()
    if (typeof is_enabled === 'boolean') {
      params.append('is_enabled', is_enabled)
    }
    if (platformUserIds) {
      params.append('platform_user_ids', platformUserIds.join(','))
    }
    const query = params.toString()
    const endpoint = query
      ? `/integrations/connect/facebook/assets?${query}`
      : '/integrations/connect/facebook/assets'
    const response = await makeApiRequest(api, 'get', endpoint)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapIntegrationDetails(data), status }
    }
    return { error, status }
  },

  triggerFacebookAssetRefetchAsync: async ({ platformUserIds } = {}) => {
    const endpoint = withPlatformUserIds('/integrations/connect/facebook/assets', platformUserIds)
    return await makeApiRequest(api, 'post', endpoint)
  },

  getFacebookAssetTaskStatusAsync: async (taskId) => {
    return await makeApiRequest(api, 'get', `/integrations/connect/facebook/task-status/${taskId}`)
  },

  toggleFacebookAssetAsync: async (assetId, enabled) => {
    return await makeApiRequest(api, 'put', `/integrations/connect/facebook/assets/${assetId}`, {
      enabled,
    })
  },

  enrichFacebookAssetsAsync: async (accounts) => {
    const response = await makeApiRequest(
      api,
      'post',
      '/integrations/connect/facebook/assets/enrich',
      {
        assets: accounts,
      },
    )
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapIntegrationDetails(data), status }
    }
    return { error, status }
  },

  // Google-scoped (app/api/v4/integrations/connect/google/assets/assets.py) —
  // ad accounts with nested conversion pixels.
  listGoogleAssetsAsync: async ({ platformUserIds } = {}) => {
    const endpoint = withPlatformUserIds('/integrations/connect/google/assets', platformUserIds)
    const response = await makeApiRequest(api, 'get', endpoint)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      const withPlatform = (data ?? []).map((item) => ({
        platform: Platform.google,
        asset_class: AssetClass.ad_account,
        ...item,
      }))
      return { data: mapIntegrationDetails(withPlatform), status }
    }
    return { error, status }
  },

  toggleGoogleAssetAsync: async (assetId, enabled) => {
    return await makeApiRequest(api, 'put', `/integrations/connect/google/assets/${assetId}`, {
      enabled,
    })
  },
}

export default integrationAssetsApi
